import { useState } from 'react';
import type { JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { t } from 'i18next';
import { EDataTableType, EModuleType, EToolbarButton } from '../../data/Constants';
import { ActionableDataTable } from '../DataTable/ActionableDataTable';
import { deleteLov, getLov, updateLov } from '../../store/states/LovSlice';
import { setAlertMessage } from '../../store/states/AlertMessageSlice';
import { LovOptionsManagement } from './LovOptionsManagement';
import type { AppDispatch, AppState } from '../../store';
import type { IDataTableRow } from '../../../types';

/**
 * List of values (LOV) maintenance screen.
 *
 * Renders the LOV maintenance table with inline edit and delete support, and opens the
 * options management panel for the LOV selected in the table.
 *
 * @returns The LOV management view
 */
export const LovManagement = (): JSX.Element => {
  const [selectedLovCode, setSelectedLovCode] = useState<string | null>(null);
  const [selectedLovName, setSelectedLovName] = useState<string>('');
  const [isSelectedLovActive, setIsSelectedLovActive] = useState<boolean>(true);
  const dispatch = useDispatch<AppDispatch>();
  const lovState = useSelector((state: AppState) => state.lovManagement.lov);

  /**
   * Delete the selected LOVs, then show a success alert and reload the current page
   *
   * @param codes - Codes of the LOVs to delete
   */
  const handleDeleteLov = (codes: string[]): void => {
    void dispatch(deleteLov({ codes })).then((res) => {
      if (res.payload && typeof res.payload === 'object' && !('error' in res.payload)) {
        dispatch(
          setAlertMessage({
            show: true,
            message: t('messages.success.delete.general'),
            type: 'success',
          })
        );
        void dispatch(
          getLov({
            page: lovState.currentPage,
            limit: lovState.recordsPerPage,
            searchCode: lovState.searchKey,
          })
        );
      }
    });
  };

  /**
   * Persist an inline row edit, then show a success alert and reload the current page
   *
   * @param _id - Row id from the table; unused because the LOV code identifies the record
   * @param values - Edited field values, where `isActive` arrives as a 'true'/'false' string
   * @param row - The original row, used to read the LOV code
   */
  const handleSaveLov = (
    _id: number | string,
    values: Record<string, string>,
    row?: IDataTableRow
  ): void => {
    void dispatch(
      updateLov({ code: row?.code ?? '', name: values.name, isActive: values.isActive === 'true' })
    ).then((res) => {
      if (res.payload && typeof res.payload === 'object' && !('error' in res.payload)) {
        dispatch(
          setAlertMessage({
            show: true,
            message: t('messages.success.update'),
            type: 'success',
          })
        );
        void dispatch(
          getLov({
            page: lovState.currentPage,
            limit: lovState.recordsPerPage,
            searchCode: lovState.searchKey,
          })
        );
      }
    });
  };

  return (
    <>
      <h3 className="mainHeader">ListOfValues</h3>
      <ActionableDataTable
        type={EDataTableType.Lov}
        moduleType={EModuleType.Lov}
        headerActions={{ [EToolbarButton.Delete]: { onClick: handleDeleteLov } }}
        loadData={({ page, limit, searchCode }) =>
          void dispatch(getLov({ page, limit, searchCode }))
        }
        rowActions={{ save: { onClick: handleSaveLov } }}
        onCellClick={(row) => {
          setSelectedLovCode(row.code ?? null);
          setSelectedLovName(String(row.name ?? ''));
          setIsSelectedLovActive(row.isActive !== false);
        }}
      />
      {selectedLovCode && (
        <LovOptionsManagement
          lovCode={selectedLovCode}
          lovName={selectedLovName}
          isLovActive={isSelectedLovActive}
          onClose={() => setSelectedLovCode(null)}
        />
      )}
    </>
  );
};
